import { useEffect, useState } from 'react';
import { AU_STATES, AU_TIMEZONES, type SettingsDTO } from '@em/shared';
import { api, qk, queryClient, useSettings } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { useToast } from '../../components/Toast';
import { Alert, Button, Card, Checkbox, ErrorAlert, Field, Input, QueryState, Select } from '../../components/ui';

const TIMEZONES = [...new Set(Object.values(AU_TIMEZONES))];

export function BusinessSettings() {
  const q = useSettings();
  return <QueryState q={q}>{(s) => <BusinessForm initial={s} />}</QueryState>;
}

function BusinessForm({ initial }: { initial: SettingsDTO }) {
  const { can, refresh } = useAuth();
  const owner = can('owner');
  const toast = useToast();
  const [f, setF] = useState<SettingsDTO>(initial);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(initial), [initial]);
  const set = <K extends keyof SettingsDTO>(k: K, v: SettingsDTO[K]) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      // Only send what changed, so an unchanged pay period definition is never re-checked.
      const changed = Object.fromEntries(Object.entries(f).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(initial[k as keyof SettingsDTO]))) as Partial<SettingsDTO>;
      if (Object.keys(changed).length === 0) {
        toast.push({ kind: 'info', title: 'Nothing to save' });
        return;
      }
      await api.updateSettings(changed);
      await queryClient.invalidateQueries({ queryKey: qk.settings });
      if (changed.businessName || changed.features) await refresh();
      toast.success('Settings saved');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <fieldset disabled={!owner} style={{ border: 0, padding: 0 }}>
        <div className="stack">
          {!owner && <Alert tone="accent">Only the owner can change business settings.</Alert>}
          <ErrorAlert error={error} />
          <Card title="Business">
            <div className="form-grid three">
              <Field label="Business name">
                <Input value={f.businessName} onChange={(e) => set('businessName', e.target.value)} required />
              </Field>
              <Field label="Default state">
                <Select value={f.defaultState} onChange={(e) => set('defaultState', e.target.value as SettingsDTO['defaultState'])}>
                  {AU_STATES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Default time zone">
                <Select value={f.defaultTimezone} onChange={(e) => set('defaultTimezone', e.target.value)}>
                  {TIMEZONES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Alert email" hint="Where system alerts are sent">
                <Input type="email" value={f.alertEmail ?? ''} onChange={(e) => set('alertEmail', e.target.value || null)} />
              </Field>
            </div>
          </Card>
          <Card title="Pay periods and approval">
            <div className="form-grid three">
              <Field label="Pay period" hint="Cannot change once timesheets exist">
                <Select value={f.payPeriodType} onChange={(e) => set('payPeriodType', e.target.value as SettingsDTO['payPeriodType'])}>
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                </Select>
              </Field>
              <Field label="Period anchor date" hint="Any date on which a period starts">
                <Input type="date" value={f.periodAnchorDate} onChange={(e) => set('periodAnchorDate', e.target.value)} />
              </Field>
              <Field label="Approval deadline (hours after period end)">
                <Input type="number" min={0} max={240} value={f.approvalDeadlineHours} onChange={(e) => set('approvalDeadlineHours', Number(e.target.value))} />
              </Field>
              <Field label="Default export method">
                <Select value={f.exportMethod} onChange={(e) => set('exportMethod', e.target.value as SettingsDTO['exportMethod'])}>
                  <option value="file">Import file</option>
                  <option value="api">MYOB API</option>
                </Select>
              </Field>
              <div className="field" style={{ justifyContent: 'flex-end', paddingBottom: 8 }}>
                <Checkbox label="Employees acknowledge their timesheet" checked={f.employeeTimesheetAck} onChange={(v) => set('employeeTimesheetAck', v)} />
              </div>
            </div>
          </Card>
          <Card title="Punching and kiosks">
            <div className="form-grid three">
              <Field label="Missed punch reminder (minutes after roster start)">
                <Input type="number" min={0} max={240} value={f.missedPunchReminderMinutes} onChange={(e) => set('missedPunchReminderMinutes', Number(e.target.value))} />
              </Field>
              <Field label="Kiosk opens">
                <Input type="time" value={f.kioskOperatingHours.start} onChange={(e) => set('kioskOperatingHours', { ...f.kioskOperatingHours, start: e.target.value })} />
              </Field>
              <Field label="Kiosk closes">
                <Input type="time" value={f.kioskOperatingHours.end} onChange={(e) => set('kioskOperatingHours', { ...f.kioskOperatingHours, end: e.target.value })} />
              </Field>
              <div className="field" style={{ justifyContent: 'flex-end', paddingBottom: 8 }}>
                <Checkbox label="Take a photo at kiosk punches" checked={f.photoCaptureEnabled} onChange={(v) => set('photoCaptureEnabled', v)} />
              </div>
              <Field label="Keep photos for (days)">
                <Input type="number" min={1} max={3650} value={f.photoRetentionDays} onChange={(e) => set('photoRetentionDays', Number(e.target.value))} />
              </Field>
              <Field label="Keep records for (years)" hint="At least 7 under the Fair Work Act">
                <Input type="number" min={7} max={30} value={f.retentionYears} onChange={(e) => set('retentionYears', Number(e.target.value))} />
              </Field>
              <div className="field" style={{ justifyContent: 'flex-end', paddingBottom: 8 }}>
                <Checkbox label="Job costing (phase 2)" checked={f.features.jobCosting} onChange={(v) => set('features', { jobCosting: v })} />
              </div>
            </div>
          </Card>
          {owner && (
            <div className="row end">
              <Button onClick={() => setF(initial)}>Reset</Button>
              <Button type="submit" variant="primary" loading={busy}>
                Save settings
              </Button>
            </div>
          )}
        </div>
      </fieldset>
    </form>
  );
}
